import PropTypes from 'prop-types';
import React, { memo, useMemo, useState } from 'react';
import classNames from 'classnames';
import { FormControl, Icon, InputLabel, OutlinedInput, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import SportScaleMaskedInput from 'common/formFields/sportScaleMaskedInput.component';

const SportScaleMaskedTextField = memo(
  ({
    disabled,
    endAdornment,
    hasInfoTooltip,
    hasValidation,
    inputProps,
    label,
    mask,
    maskPlaceholder,
    name,
    placeholder,
    readOnly,
    tooltipText,
    startAdornment,
    type,
    validatorRef,
    value,
    onChange,
    ...restProps
  }) => {
    const classes = useStyles();

    const [inputLabelRef, setInputLabelRef] = useState(undefined);

    const renderedLabel = useMemo(
      () =>
        hasInfoTooltip ? (
          <InputLabel
            ref={ref => {
              setInputLabelRef(ref);
            }}
            classes={{ root: classes.inputLabelRoot }}
            shrink={Boolean(value)}
          >
            {label}
            <Tooltip classes={{ tooltip: classes.tooltip }} title={tooltipText}>
              <Icon className={classNames(classes.infoIcon, 'fal fa-info-circle')} />
            </Tooltip>
          </InputLabel>
        ) : (
          <InputLabel
            ref={ref => {
              setInputLabelRef(ref);
            }}
            shrink={Boolean(value)}
          >
            {label}
          </InputLabel>
        ),
      [classes, hasInfoTooltip, label, tooltipText, value, setInputLabelRef]
    );

    return (
      <FormControl
        ref={validatorRef}
        classes={{
          // if this component is wrapped in a validation component we dont want
          // a bottom margin added because the validation will add it so the
          // margin includes any error messages
          root: hasValidation ? classes.formControlNoMargin : classes.formControl,
        }}
        disabled={disabled}
        variant="outlined"
        {...restProps}
      >
        {label && renderedLabel}
        <OutlinedInput
          className={classes.input}
          classes={{
            adornedEnd: endAdornment ? classes.endAdorned : '',
            adornedStart: startAdornment ? classes.startAdornment : '',
          }}
          endAdornment={endAdornment}
          inputProps={{ ...inputProps, mask, maskPlaceholder, onChange }}
          inputComponent={SportScaleMaskedInput}
          labelWidth={inputLabelRef ? inputLabelRef.offsetWidth : 0}
          name={name}
          notched={Boolean(value)}
          readOnly={readOnly}
          startAdornment={startAdornment}
          type={type}
          value={value}
        />
      </FormControl>
    );
  }
);

const useStyles = makeStyles(theme => ({
  input: {
    paddingRight: 0,
  },
  formControl: {
    borderRadius: '5%',
    flex: 1,
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    minWidth: '10rem',
  },
  formControlNoMargin: {
    marginBottom: 0,
  },
  endAdorned: {
    paddingRight: theme.spacing(1.5),
  },
  startAdornment: {
    paddingLeft: theme.spacing(1.5),
  },
  tooltip: {
    fontSize: '.75rem',
  },
  infoIcon: {
    fontSize: '1rem',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  inputLabelRoot: {
    alignItems: 'center',
    display: 'flex',
    pointerEvents: 'auto',
  },
}));

SportScaleMaskedTextField.propTypes = {
  disabled: PropTypes.bool,
  endAdornment: PropTypes.node,
  hasInfoTooltip: PropTypes.bool,
  hasValidation: PropTypes.bool,
  inputProps: PropTypes.object,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  mask: PropTypes.array.isRequired,
  maskPlaceholder: PropTypes.string.isRequired,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  startAdornment: PropTypes.node,
  tooltipText: PropTypes.string,
  type: PropTypes.string,
  validatorRef: PropTypes.object,
  value: PropTypes.any,

  onChange: PropTypes.func,
};

SportScaleMaskedTextField.defaultProps = {
  disabled: false,
  endAdornment: null,
  hasInfoTooltip: false,
  hasValidation: false,
  readOnly: false,
  inputProps: {},
  label: '',
  name: '',
  placeholder: '',
  startAdornment: null,
  tooltipText: '',
  type: 'input',
  validatorRef: React.createRef(),
  value: '',
  onChange: () => {},
};

export default SportScaleMaskedTextField;
